import React, { useState, useEffect } from 'react'
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Divider,
  InputNumber
} from 'antd'
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { apiRequest } from '../../utils/api'

const { Option } = Select

function FieldManagement() {
  const [fields, setFields] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [form] = Form.useForm()
  
  // 获取字段列表
  useEffect(() => {
    fetchFields()
  }, [])
  
  const fetchFields = async () => {
    setLoading(true)
    try {
      const response = await apiRequest('/admin/fields')
      const data = await response.json()
      
      if (response.ok) {
        setFields(data.fields || [])
      }
    } catch (error) {
      console.error('获取字段列表失败:', error)
      message.error('获取字段列表失败')
    } finally {
      setLoading(false)
    }
  }
  
  const handleAddField = () => {
    setEditingField(null)
    form.resetFields()
    setModalVisible(true)
  }
  
  const handleEditField = (field) => {
    setEditingField(field)
    form.setFieldsValue({
      field_name: field.field_name,
      field_type: field.field_type,
      display_name: field.display_name,
      is_required: field.is_required,
      is_searchable: field.is_searchable,
      sort_order: field.sort_order
    })
    setModalVisible(true)
  }
  
  const handleDeleteField = async (fieldId) => {
    try {
      const response = await apiRequest(`/admin/fields/${fieldId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        message.success('字段删除成功')
        fetchFields()
      } else {
        const data = await response.json()
        message.error(data.error || '字段删除失败')
      }
    } catch (error) {
      console.error('删除字段失败:', error)
      message.error('删除字段失败')
    }
  }
  
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      
      const method = editingField ? 'PUT' : 'POST'
      const url = editingField 
        ? `/api/admin/fields/${editingField.id}` 
        : '/api/admin/fields'
      
      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(values)
      })
      
      if (response.ok) {
        message.success(editingField ? '字段更新成功' : '字段添加成功')
        setModalVisible(false)
        form.resetFields()
        fetchFields()
      } else {
        const data = await response.json()
        message.error(data.error || (editingField ? '字段更新失败' : '字段添加失败'))
      }
    } catch (error) {
      console.error('保存字段失败:', error)
      message.error('保存字段失败')
    }
  }
  
  const columns = [
    {
      title: '字段名称',
      dataIndex: 'field_name',
      key: 'field_name',
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: '字段类型',
      dataIndex: 'field_type',
      key: 'field_type',
      render: (type) => {
        const typeMap = {
          'text': '文本',
          'number': '数字',
          'date': '日期',
          'boolean': '布尔值',
          'select': '选择框'
        }
        return typeMap[type] || type
      }
    },
    {
      title: '必填',
      dataIndex: 'is_required',
      key: 'is_required',
      render: (isRequired) => (
        <Switch checked={isRequired} disabled size="small" />
      )
    },
    {
      title: '可搜索',
      dataIndex: 'is_searchable',
      key: 'is_searchable',
      render: (isSearchable) => (
        <Switch checked={isSearchable} disabled size="small" />
      )
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      sorter: (a, b) => a.sort_order - b.sort_order,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditField(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个字段吗？"
            description="删除字段将清除所有书籍的该字段值"
            onConfirm={() => handleDeleteField(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]
  
  return (
    <div className="field-management">
      <Card className="management-card">
        <div className="management-header">
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddField}
            >
              添加字段
            </Button>
          </Space>
        </div>
        
        <Divider />
        
        <Table
          dataSource={fields}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
      
      {/* 添加/编辑字段模态框 */}
      <Modal
        title={editingField ? "编辑字段" : "添加字段"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
        }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="field_name"
                label="字段名称"
                rules={[{ required: true, message: '请输入字段名称' }]}
              >
                <Input 
                  placeholder="请输入字段名称（英文）" 
                  disabled={!!editingField}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="field_type"
                label="字段类型"
                rules={[{ required: true, message: '请选择字段类型' }]}
              >
                <Select placeholder="请选择字段类型">
                  <Option value="text">文本</Option>
                  <Option value="number">数字</Option>
                  <Option value="date">日期</Option>
                  <Option value="boolean">布尔值</Option>
                  <Option value="select">选择框</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="显示名称"
                rules={[{ required: true, message: '请输入显示名称' }]}
              >
                <Input placeholder="请输入显示名称" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="sort_order"
                label="排序顺序"
                rules={[{ required: true, message: '请输入排序顺序' }]}
              >
                <InputNumber 
                  min={0} 
                  placeholder="请输入排序顺序" 
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_required"
                label="是否必填"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="is_searchable"
                label="是否可搜索"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default FieldManagement